"use client";

import { lazy, type ReactNode, Suspense } from "react";
import { initializeAnalytics } from "../analytics-runtime";
import type { AutoTrackingOptions } from "../auto-tracking";
import type { AnalyticsProviderConfig } from "../types";
import { AnalyticsAutoTracker } from "./AnalyticsAutoTracker";
import { AnalyticsPageView } from "./AnalyticsPageView";

export type AnalyticsProviderProps = AnalyticsProviderConfig & {
	children: ReactNode | ReactNode[];
	trackPageViews?: boolean;
	shouldFlush?: (pathname: string) => boolean;
	autoTracking?: boolean | AutoTrackingOptions;
};

export { getAnalytics, initializeAnalytics } from "../analytics-runtime";

// Lazy-load PostHogProvider from posthog-js/react so posthog-js is only bundled when used
const LazyPostHogProvider = lazy(async () => {
	const module = await import("./PostHogProvider");
	return { default: module.PostHogProvider };
});

export function AnalyticsProvider({
	children,
	trackPageViews = true,
	shouldFlush,
	autoTracking = true,
	...config
}: AnalyticsProviderProps) {
	initializeAnalytics(config);

	const pageViewComponent = trackPageViews ? (
		<Suspense fallback={null}>
			<AnalyticsPageView shouldFlush={shouldFlush} />
		</Suspense>
	) : null;

	const trackingComponent = autoTracking ? (
		<AnalyticsAutoTracker options={typeof autoTracking === "object" ? autoTracking : {}} />
	) : null;

	if (config.providers.includes("posthog")) {
		return (
			<Suspense fallback={children}>
				<LazyPostHogProvider>
					{pageViewComponent}
					{trackingComponent}
					{children}
				</LazyPostHogProvider>
			</Suspense>
		);
	}

	return (
		<>
			{pageViewComponent}
			{trackingComponent}
			{children}
		</>
	);
}
